import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Page } from "@models/page";
import {
  CreateSupportPayload,
  Support,
  SupportStatus,
  SupportSummary,
  UpdateSupportPayload,
} from "@models/support";
import { PaginatorState } from "primeng/paginator";
import { catchError, map, Observable, of } from "rxjs";
import { environment } from "../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class SupportsService {
  private readonly _http = inject(HttpClient);
  private readonly baseUrl = `${environment.API_BACKEND}/supports`;

  getSupports(
    pageEvent?: PaginatorState | null,
    searchQuery?: string | null,
    status?: SupportStatus | null,
  ): Observable<Page<Support>> {
    const perPage = pageEvent?.rows ?? 20;
    let params = new HttpParams()
      .set("page", (pageEvent?.page ?? 0) + 1)
      .set("per_page", perPage);

    if (searchQuery) {
      params = params.set("search_query", searchQuery);
    }

    if (status) {
      params = params.set("status", status);
    }

    return this._http.get<Page<Support>>(this.baseUrl, { params }).pipe(
      // A API já retorna page_size no meta; o perPage fica só como fallback.
      map((page) => ({
        ...page,
        meta: { page_size: perPage, ...page.meta },
      })),
      // A listagem retorna 404 quando não há nenhum chamado visível ou quando
      // os filtros não retornam nada. No FE isso é "nenhum resultado", não erro.
      catchError(() =>
        of({
          data: [],
          meta: {
            total_count: 0,
            total_pages: 0,
            current_page: 0,
            page_size: perPage,
          },
        } as Page<Support>),
      ),
    );
  }

  // Contadores por status para os cards do topo (respeita o mesmo escopo do
  // index). Para o painel completo, NÃO envie `status` (ele zera os demais).
  getSummary(
    searchQuery?: string | null,
    status?: SupportStatus | null,
  ): Observable<SupportSummary> {
    let params = new HttpParams();

    if (searchQuery) {
      params = params.set("search_query", searchQuery);
    }

    if (status) {
      params = params.set("status", status);
    }

    return this._http
      .get<SupportSummary>(`${this.baseUrl}/summary`, { params })
      .pipe(
        catchError(() =>
          of({
            pending: 0,
            in_progress: 0,
            finished: 0,
            canceled: 0,
          } as SupportSummary),
        ),
      );
  }

  getById(id: number): Observable<Support> {
    return this._http.get<Support>(`${this.baseUrl}/${id}`);
  }

  create(payload: CreateSupportPayload): Observable<Support> {
    // O payload deve estar aninhado sob a chave "support" (padrão Rails).
    return this._http.post<Support>(this.baseUrl, { support: payload });
  }

  update(id: number, payload: UpdateSupportPayload): Observable<Support> {
    return this._http.put<Support>(`${this.baseUrl}/${id}`, {
      support: payload,
    });
  }
}
